import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useThemeMode } from "hooks/useThemeMode";
import { useCallback, useMemo, useState } from "react";
import { Outlet, useMatch } from "react-router-dom";
import { useBreadcrumb } from "../../../hooks/useBreadcrumbs";
import { StatsProvider, useStats } from "./StatsContext";
import { OmnisearchProvider } from "providers/OmnisearchContext";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import {
  parseOmnisearch,
  stringifyOmnisearch,
} from "components/Omnisearch/FilterPopover";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExportParams, useExportData } from "hooks/useExportData";
import { WasteSubmissionsDataGrid } from "./WasteSubmissionsDataGrid";
import { SummaryOverview } from "./Summary/SummaryOverview";
import { WasteFulfillmentSummaryFilter } from "./WasteFulfillmentSummaryContext";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import {
  RegulatoryStatusOption,
  SubmissionStatus,
  SubmissionMaterializationType,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import FileDownload from "@mui/icons-material/FileDownload";

// Status filter options for multi-select checkbox menu
const STATUS_FILTER_OPTIONS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

// All statuses
const ALL_STATUSES = Object.keys(STATUS_FILTER_OPTIONS) as SubmissionStatus[];

// Default = All statuses except completed (Filing Complete, Not Reporting)
const DEFAULT_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.NotStarted,
  SubmissionStatus.InProgress,
  SubmissionStatus.EncampInitialReview,
  SubmissionStatus.InReview,
  SubmissionStatus.ProcessingSubmission,
];

// US States list for filtering
const US_STATES = Object.keys(STATE_NAMES);

// Generator status filter options
const GENERATOR_STATUS_OPTIONS: {
  label: string;
  value: RegulatoryStatusOption;
}[] = [
  { label: "LQG", value: RegulatoryStatusOption.LargeQuantityGenerator },
  { label: "SQG", value: RegulatoryStatusOption.SmallQuantityGenerator },
  { label: "VSQG", value: RegulatoryStatusOption.VerySmallQuantityGenerator },
  { label: "None", value: RegulatoryStatusOption.None },
];

const ALL_GENERATOR_STATUSES = [
  RegulatoryStatusOption.LargeQuantityGenerator,
  RegulatoryStatusOption.SmallQuantityGenerator,
  RegulatoryStatusOption.VerySmallQuantityGenerator,
  RegulatoryStatusOption.None,
];

// Date picker shortcuts
const datePickerShortcuts: PickersShortcutsProps<DateRange<DateTime>>["items"] =
  [
    {
      label: "This Quarter",
      getValue: () => [
        DateTime.now().startOf("quarter"),
        DateTime.now().endOf("quarter"),
      ],
    },
    {
      label: "Next Quarter",
      getValue: () => [
        DateTime.now().plus({ quarters: 1 }).startOf("quarter"),
        DateTime.now().plus({ quarters: 1 }).endOf("quarter"),
      ],
    },
    {
      label: "Next 90 Days",
      getValue: () => [DateTime.now(), DateTime.now().plus({ days: 90 })],
    },
    {
      label: "Next 6 Months",
      getValue: () => [DateTime.now(), DateTime.now().plus({ months: 6 })],
    },
    {
      label: "This Year",
      getValue: () => [
        DateTime.now().startOf("year"),
        DateTime.now().endOf("year"),
      ],
    },
    {
      label: "Reset",
      getValue: () => [null, null],
    },
  ];

// Helper to extract a key's value from an omnisearch string
function extractOmnisearchValue(
  omnisearch: string | null,
  key: string
): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  if (match) {
    return match[1] ?? match[2];
  }
  return undefined;
}

// Helper to compare arrays (order-independent)
function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

function WasteFulfillmentDashboardContent() {
  const { themeMode } = useThemeMode();
  const isOverviewRoute = useMatch("/staff/fulfillment/waste");

  const { statsOpen, handleToggle } = useStats();

  const {
    omnisearch,
    getOmnisearchValue,
    setOmnisearch,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  } = useOmnisearchInput({
    isURLDriven: true,
    initialFilters: [],
  });

  // Default date range: start of today to end of day 90 days from now
  // Using startOf('day') ensures we include all items due today, not just
  // items due after the current time
  const defaultDateRange = useMemo(() => {
    const startOfToday = DateTime.now().startOf("day");
    const endOfNinetyDays = startOfToday.plus({ days: 90 }).endOf("day");
    return {
      startDate: startOfToday.toISO() ?? "",
      endDate: endOfNinetyDays.toISO() ?? "",
      startDateTime: startOfToday,
      endDateTime: endOfNinetyDays,
    };
  }, []);

  // Include overdue toggle - defaults to true
  const includeOverdue = useMemo(() => {
    const value = getOmnisearchValue("includeOverdue");
    return value !== "false";
  }, [getOmnisearchValue]);

  // Calculate the overdue cutoff date (3 months ago) for display
  const overdueCutoffDate = useMemo(() => {
    return DateTime.now().minus({ months: 3 }).toFormat("MMM d, yyyy");
  }, []);

  const handleIncludeOverdueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const currentFilters = parseOmnisearch(omnisearch);

      if (checked) {
        delete currentFilters.includeOverdue;
      } else {
        currentFilters.includeOverdue = "false";
      }

      setOmnisearch(stringifyOmnisearch(currentFilters));
    },
    [omnisearch, setOmnisearch]
  );

  // Only overdue filter - derived from omnisearch
  const onlyOverdue = useMemo(() => {
    const value = getOmnisearchValue("onlyOverdue");
    return value === "true";
  }, [getOmnisearchValue]);

  // Materialization type filter - derived from omnisearch
  // Allows filtering by VIRTUAL_EVENT, MATERIALIZED, ONE_OFF or any combination
  const materializationTypes = useMemo(() => {
    const value = getOmnisearchValue("materializationType");
    if (!value) return undefined;
    
    // Parse pipe-separated values (e.g., "VIRTUAL_EVENT|MATERIALIZED")
    const types = value.split("|").filter((t): t is SubmissionMaterializationType => 
      Object.values(SubmissionMaterializationType).includes(t as SubmissionMaterializationType)
    );
    
    return types.length > 0 ? types : undefined;
  }, [getOmnisearchValue]);

  // Handler for clicking on the "Overdue" stat in the summary
  const handleOverdueClick = useCallback(() => {
    const currentFilters = parseOmnisearch(omnisearch);

    if (onlyOverdue) {
      // Toggle off - remove the filter
      delete currentFilters.onlyOverdue;
    } else {
      // Toggle on - set filter to only show overdue
      currentFilters.onlyOverdue = "true";
    }

    setOmnisearch(stringifyOmnisearch(currentFilters));
  }, [omnisearch, onlyOverdue, setOmnisearch]);

  // Assigned to me toggle
  const assignedToMe = useMemo(() => {
    return getOmnisearchValue("assignedToMe") === "true";
  }, [getOmnisearchValue]);

  const handleAssignedToMeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const currentFilters = parseOmnisearch(omnisearch);

      if (checked) {
        currentFilters.assignedToMe = "true";
      } else {
        delete currentFilters.assignedToMe;
      }

      setOmnisearch(stringifyOmnisearch(currentFilters));
    },
    [omnisearch, setOmnisearch]
  );

  // Status filter state
  const [statusOpen, setStatusOpen] = useState(false);
  const statusFilterValue = extractOmnisearchValue(omnisearch, "status");

  const committedStatuses = useMemo(() => {
    if (statusFilterValue === undefined || statusFilterValue === "") {
      return [...DEFAULT_STATUSES];
    }
    if (statusFilterValue === "none") {
      return [];
    }
    const parsedStatuses = statusFilterValue
      .split(/[|,]/)
      .filter(
        (s) => s.length > 0 && ALL_STATUSES.includes(s as SubmissionStatus)
      );
    return parsedStatuses.length > 0 ? parsedStatuses : [...DEFAULT_STATUSES];
  }, [statusFilterValue]);

  const [intermediateStatuses, setIntermediateStatuses] = useState<string[]>(
    []
  );
  const selectedStatuses = statusOpen ? intermediateStatuses : committedStatuses;

  const handleStatusClose = useCallback(() => {
    setStatusOpen(false);
    if (intermediateStatuses.length === 0) {
      editOmnisearchKeyValues([["status", "none"]]);
    } else if (
      arraysEqual(
        [...intermediateStatuses].sort(),
        [...DEFAULT_STATUSES].sort()
      )
    ) {
      removeOmnisearchKeyValue("status");
    } else {
      editOmnisearchKeyValues([["status", intermediateStatuses.join("|")]]);
    }
  }, [intermediateStatuses, editOmnisearchKeyValues, removeOmnisearchKeyValue]);

  const handleStatusOpen = useCallback(() => {
    setIntermediateStatuses([...committedStatuses]);
    setStatusOpen(true);
  }, [committedStatuses]);

  const handleStatusesChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const raw = e.target.value as string[];
      if (raw.some((v) => v === null || v === undefined)) {
        return;
      }
      const values = raw.filter(
        (v): v is string =>
          v !== null &&
          v !== undefined &&
          v !== "all" &&
          typeof v === "string" &&
          ALL_STATUSES.includes(v as SubmissionStatus)
      );
      setIntermediateStatuses(values);
    },
    []
  );

  const handleToggleAllStatuses = useCallback(() => {
    if (intermediateStatuses.length === ALL_STATUSES.length) {
      setIntermediateStatuses([]);
    } else {
      setIntermediateStatuses([...ALL_STATUSES]);
    }
  }, [intermediateStatuses.length]);

  const handleResetStatuses = useCallback(() => {
    setIntermediateStatuses([...DEFAULT_STATUSES]);
  }, []);

  const isStatusFilterAtDefault = useMemo(() => {
    const statuses = statusOpen ? intermediateStatuses : committedStatuses;
    if (statuses.length !== DEFAULT_STATUSES.length) return false;
    return DEFAULT_STATUSES.every((s) => statuses.includes(s));
  }, [statusOpen, intermediateStatuses, committedStatuses]);

  // State filter state
  const [stateOpen, setStateOpen] = useState(false);
  const statesFilterValue = extractOmnisearchValue(omnisearch, "state");

  const committedStates = useMemo(() => {
    if (statesFilterValue === undefined || statesFilterValue === "") {
      return [...US_STATES];
    }
    if (statesFilterValue === "none") {
      return [];
    }
    return statesFilterValue.split(/[|,]/).filter((s) => s.length > 0);
  }, [statesFilterValue]);

  const [intermediateStates, setIntermediateStates] = useState<string[]>([]);
  const selectedStates = stateOpen ? intermediateStates : committedStates;

  const handleStateClose = useCallback(() => {
    setStateOpen(false);
    if (intermediateStates.length === 0) {
      editOmnisearchKeyValues([["state", "none"]]);
    } else if (intermediateStates.length === US_STATES.length) {
      removeOmnisearchKeyValue("state");
    } else {
      editOmnisearchKeyValues([["state", intermediateStates.join("|")]]);
    }
  }, [intermediateStates, editOmnisearchKeyValues, removeOmnisearchKeyValue]);

  const handleStateOpen = useCallback(() => {
    setIntermediateStates([...committedStates]);
    setStateOpen(true);
  }, [committedStates]);

  const handleStatesChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value as string[];
    if (raw.some((v) => v === null || v === undefined)) {
      return;
    }
    const values = raw.filter(
      (v) =>
        v !== "select-all" &&
        v !== null &&
        v !== undefined &&
        US_STATES.includes(v)
    );
    setIntermediateStates(values);
  }, []);

  const handleToggleAllStates = useCallback(() => {
    if (intermediateStates.length === US_STATES.length) {
      setIntermediateStates([]);
    } else {
      setIntermediateStates([...US_STATES]);
    }
  }, [intermediateStates.length]);

  const handleResetStates = useCallback(() => {
    setIntermediateStates([...US_STATES]);
  }, []);

  const isStateFilterAtDefault = selectedStates.length === US_STATES.length;

  // Generator status filter state
  const [generatorStatusOpen, setGeneratorStatusOpen] = useState(false);
  const generatorStatusFilterValue = extractOmnisearchValue(
    omnisearch,
    "generatorStatus"
  );

  const committedGeneratorStatuses = useMemo(() => {
    if (
      generatorStatusFilterValue === undefined ||
      generatorStatusFilterValue === ""
    ) {
      return [...ALL_GENERATOR_STATUSES];
    }
    if (generatorStatusFilterValue === "none") {
      return [RegulatoryStatusOption.None];
    }
    const parsedStatuses = generatorStatusFilterValue
      .split(/[|,]/)
      .filter(
        (s) =>
          s.length > 0 &&
          ALL_GENERATOR_STATUSES.includes(s as RegulatoryStatusOption)
      ) as RegulatoryStatusOption[];
    return parsedStatuses.length > 0
      ? parsedStatuses
      : [...ALL_GENERATOR_STATUSES];
  }, [generatorStatusFilterValue]);

  const [intermediateGeneratorStatuses, setIntermediateGeneratorStatuses] =
    useState<RegulatoryStatusOption[]>([]);
  const selectedGeneratorStatuses = generatorStatusOpen
    ? intermediateGeneratorStatuses
    : committedGeneratorStatuses;

  const handleGeneratorStatusClose = useCallback(() => {
    setGeneratorStatusOpen(false);
    if (intermediateGeneratorStatuses.length === 0) {
      editOmnisearchKeyValues([
        ["generatorStatus", RegulatoryStatusOption.None],
      ]);
    } else if (
      intermediateGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length
    ) {
      removeOmnisearchKeyValue("generatorStatus");
    } else {
      editOmnisearchKeyValues([
        ["generatorStatus", intermediateGeneratorStatuses.join("|")],
      ]);
    }
  }, [
    intermediateGeneratorStatuses,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  ]);

  const handleGeneratorStatusOpen = useCallback(() => {
    setIntermediateGeneratorStatuses([...committedGeneratorStatuses]);
    setGeneratorStatusOpen(true);
  }, [committedGeneratorStatuses]);

  const handleGeneratorStatusChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const raw = e.target.value as string[];
      if (raw.some((v) => v === null || v === undefined)) {
        return;
      }
      const values = raw.filter(
        (v) =>
          v !== "select-all" &&
          v !== null &&
          v !== undefined &&
          ALL_GENERATOR_STATUSES.includes(v as RegulatoryStatusOption)
      ) as RegulatoryStatusOption[];
      setIntermediateGeneratorStatuses(values);
    },
    []
  );

  const handleToggleAllGeneratorStatuses = useCallback(() => {
    if (
      intermediateGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length
    ) {
      setIntermediateGeneratorStatuses([]);
    } else {
      setIntermediateGeneratorStatuses([...ALL_GENERATOR_STATUSES]);
    }
  }, [intermediateGeneratorStatuses.length]);

  const handleResetGeneratorStatuses = useCallback(() => {
    setIntermediateGeneratorStatuses([...ALL_GENERATOR_STATUSES]);
  }, []);

  const isGeneratorStatusFilterAtDefault =
    selectedGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length;

  // Date range filter state
  // IMPORTANT: Use UTC zone when parsing date-only strings to match how the backend
  // resolver parses dates. Without this, Luxon interprets date-only strings in local
  // timezone, causing events at midnight UTC to be counted differently by the charts
  // vs the datagrid.
  const dueDateValue = extractOmnisearchValue(omnisearch, "dueDate");
  const [dueDateStart, dueDateEnd] = useMemo(() => {
    if (!dueDateValue) {
      return [defaultDateRange.startDateTime, defaultDateRange.endDateTime];
    }
    const parts = dueDateValue.split("..");
    const start = parts[0] ? DateTime.fromISO(parts[0], { zone: "utc" }) : null;
    const end = parts[1] ? DateTime.fromISO(parts[1], { zone: "utc" }) : null;
    return [start, end];
  }, [dueDateValue, defaultDateRange]);

  const isDueDateFilterAtDefault = useMemo(() => {
    if (!dueDateStart || !dueDateEnd) return false;
    // Compare just the dates (ignore time), check if within a day of defaults
    const startDiff = Math.abs(
      dueDateStart.diff(defaultDateRange.startDateTime, "days").days
    );
    const endDiff = Math.abs(
      dueDateEnd.diff(defaultDateRange.endDateTime, "days").days
    );
    return startDiff < 1 && endDiff < 1;
  }, [dueDateStart, dueDateEnd, defaultDateRange]);

  const handleDueDateRangeChange = useCallback(
    (newValue: DateRange<DateTime>) => {
      const [start, end] = newValue;
      if (!start && !end) {
        removeOmnisearchKeyValue("dueDate");
      } else {
        const startStr = start?.toISODate() ?? "";
        const endStr = end?.toISODate() ?? "";
        const newDateRange = `${startStr}..${endStr}`;
        editOmnisearchKeyValues([["dueDate", newDateRange]]);
      }
    },
    [editOmnisearchKeyValues, removeOmnisearchKeyValue]
  );

  // Check if ALL filters are at their default values
  const areFiltersAtDefault = useMemo(() => {
    return (
      isStatusFilterAtDefault &&
      isStateFilterAtDefault &&
      isGeneratorStatusFilterAtDefault &&
      isDueDateFilterAtDefault &&
      !assignedToMe &&
      includeOverdue &&
      !onlyOverdue
    );
  }, [
    isStatusFilterAtDefault,
    isStateFilterAtDefault,
    isGeneratorStatusFilterAtDefault,
    isDueDateFilterAtDefault,
    assignedToMe,
    includeOverdue,
    onlyOverdue,
  ]);

  // Handler for resetting all filters to default
  const handleResetFilters = useCallback(() => {
    const keysToRemove = new Set([
      "status",
      "dueDate",
      "state",
      "generatorStatus",
      "assignedToMe",
      "includeOverdue",
      "onlyOverdue",
    ]);

    const tokens: string[] = [];
    let current = "";
    let inQuotes = false;
    let quoteChar = "";
    for (let i = 0; i < omnisearch.length; i++) {
      const char = omnisearch[i];
      if (!inQuotes && (char === '"' || char === "'")) {
        inQuotes = true;
        quoteChar = char;
        current += char;
      } else if (inQuotes && char === quoteChar) {
        inQuotes = false;
        quoteChar = "";
        current += char;
      } else if (!inQuotes && char === " ") {
        if (current.trim()) {
          tokens.push(current.trim());
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current.trim()) tokens.push(current.trim());

    const filteredTokens = tokens.filter((token) => {
      const colonIndex = token.indexOf(":");
      if (colonIndex > 0) {
        const key = token.substring(0, colonIndex);
        return !keysToRemove.has(key);
      }
      return true;
    });

    setOmnisearch(filteredTokens.join(" "));
  }, [omnisearch, setOmnisearch]);

  // Compute actual date range for the query (use omnisearch dates if set, else defaults)
  const queryStartDate = dueDateStart?.toISO() ?? defaultDateRange.startDate;
  const queryEndDate = dueDateEnd?.toISO() ?? defaultDateRange.endDate;

  // Build default search string for the datagrid
  // This ensures that default filters (status) are applied when omnisearch is empty
  // and get properly merged with user filters in the URL
  // Note: Date range is passed as additionalVariables rather than omnisearch
  // because the backend uses explicit startDate/endDate params
  const defaultSearch = useMemo(() => {
    const parts: string[] = [];

    // Default status filter (excludes completed statuses)
    parts.push(`status:${DEFAULT_STATUSES.join("|")}`);

    return parts.join(" ");
  }, []);

  // Effective search: default filters merged with user's omnisearch (state, status, generatorStatus, assignedToMe, etc.)
  // Used by stats, datagrid, and export so they all see the same filters
  const effectiveSearch = useMemo((): string | null => {
    if (!omnisearch && defaultSearch) {
      return defaultSearch;
    }
    if (omnisearch && defaultSearch) {
      const userFilters = parseOmnisearch(omnisearch);
      const defaultFilters = parseOmnisearch(defaultSearch);
      const mergedFilters: Record<string, string> = {};
      for (const [key, value] of Object.entries(defaultFilters)) {
        if (!(key in userFilters)) {
          mergedFilters[key] = value;
        }
      }
      for (const [key, value] of Object.entries(userFilters)) {
        mergedFilters[key] = value;
      }
      return stringifyOmnisearch(mergedFilters);
    }
    return omnisearch || null;
  }, [omnisearch, defaultSearch]);

  // Build filter object for the stats/charts query
  // This is an object (not a string) matching the waste submissions pattern
  // The context has internal defaults, so we only include values that differ
  const statsFilter = useMemo((): WasteFulfillmentSummaryFilter | undefined => {
    const filter: WasteFulfillmentSummaryFilter = {};

    if (effectiveSearch) {
      filter.search = effectiveSearch;
    }

    // Include date range
    if (queryStartDate) {
      filter.startDate = queryStartDate;
    }
    if (queryEndDate) {
      filter.endDate = queryEndDate;
    }

    // Include overdue flags
    filter.includeOverdue = includeOverdue;
    filter.onlyOverdue = onlyOverdue;

    // Include materialization type filter if specified
    if (materializationTypes && materializationTypes.length > 0) {
      filter.materializationTypes = materializationTypes;
    }

    // Return undefined if empty (context will use internal defaults)
    // But since we always have some values, return the filter
    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [effectiveSearch, queryStartDate, queryEndDate, includeOverdue, onlyOverdue, materializationTypes]);

  useBreadcrumb({
    label: "Waste Fulfillment",
    to: "/staff/fulfillment/waste",
  });

  const alert = useAlerts();
  const { startExport, status: exportStatus } = useExportData({
    autoDownload: true,
    timeoutInSeconds: 300,
  });

  const handleExportCsv = useCallback(() => {
    const params: ExportParams = {
      exportType: "wasteSubmissionsForStaff",
      fileName: "waste-submissions-staff",
      filter: {
        search: effectiveSearch ?? undefined,
        sort: [{ field: "deadline", sort: "asc" }],
        includeOverdue,
        onlyOverdue,
        startDate: queryStartDate ?? undefined,
        endDate: queryEndDate ?? undefined,
        materializationTypes: materializationTypes ?? undefined,
      },
    };
    alert.info("Export started; download will open when ready.");
    startExport(params);
  }, [
    startExport,
    alert,
    effectiveSearch,
    includeOverdue,
    onlyOverdue,
    queryStartDate,
    queryEndDate,
    materializationTypes,
  ]);

  const exportLoadingOrPolling = exportStatus === "loading";

  return (
    <Stack sx={{ background: blueGrey[themeMode === "light" ? 50 : 900] }}>
      {/* Filters and Stats section with white background */}
      {isOverviewRoute && (
        <Stack
          paddingX={2}
          paddingY={1.5}
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
          >
            <Stack
              direction="row"
              gap={1.5}
              alignItems="flex-end"
              flexWrap="wrap"
            >
              {/* Status Filter */}
              <FormControl variant="standard" size="small" sx={{ width: 140 }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  open={statusOpen}
                  onOpen={handleStatusOpen}
                  onClose={handleStatusClose}
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusesChange}
                  label="Status"
                  variant="standard"
                  inputProps={{
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                    "data-1p-ignore": "true",
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  renderValue={(selected) => {
                    if (selected.length === ALL_STATUSES.length) {
                      return `All (${selected.length})`;
                    }
                    if (selected.length <= 2) {
                      return (selected as string[])
                        .map(
                          (s) =>
                            STATUS_FILTER_OPTIONS[s as SubmissionStatus] || s
                        )
                        .join(", ");
                    }
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{
                    MenuListProps: {
                      autoFocusItem:
                        selectedStatuses.length === ALL_STATUSES.length
                          ? false
                          : true,
                    },
                    PaperProps: {
                      sx: { minWidth: 280 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      pl: 2,
                      pr: 1,
                      py: 0.75,
                      borderBottom: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selectedStatuses.length === ALL_STATUSES.length
                          }
                          indeterminate={
                            selectedStatuses.length > 0 &&
                            selectedStatuses.length < ALL_STATUSES.length
                          }
                          onChange={handleToggleAllStatuses}
                        />
                      }
                      label="Show/Hide All"
                      sx={{ margin: 0 }}
                    />
                    {!isStatusFilterAtDefault && (
                      <Button onClick={handleResetStatuses}>Reset</Button>
                    )}
                  </Box>
                  {Object.entries(STATUS_FILTER_OPTIONS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      <Checkbox checked={selectedStatuses.includes(key)} />
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Generator Status Filter */}
              <FormControl variant="standard" size="small" sx={{ width: 140 }}>
                <InputLabel id="generator-status-filter-label">
                  Gen Status
                </InputLabel>
                <Select
                  labelId="generator-status-filter-label"
                  open={generatorStatusOpen}
                  onOpen={handleGeneratorStatusOpen}
                  onClose={handleGeneratorStatusClose}
                  multiple
                  value={selectedGeneratorStatuses}
                  onChange={handleGeneratorStatusChange}
                  label="Gen Status"
                  variant="standard"
                  inputProps={{
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                    "data-1p-ignore": "true",
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  renderValue={(selected) => {
                    if (selected.length === ALL_GENERATOR_STATUSES.length) {
                      return `All (${selected.length})`;
                    }
                    return selected
                      .map(
                        (s) =>
                          GENERATOR_STATUS_OPTIONS.find((o) => o.value === s)
                            ?.label ?? s
                      )
                      .join(", ");
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { minWidth: 280 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      pl: 2,
                      pr: 1,
                      py: 0.75,
                      borderBottom: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      minWidth: 240,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selectedGeneratorStatuses.length ===
                            ALL_GENERATOR_STATUSES.length
                          }
                          indeterminate={
                            selectedGeneratorStatuses.length > 0 &&
                            selectedGeneratorStatuses.length <
                              ALL_GENERATOR_STATUSES.length
                          }
                          onChange={handleToggleAllGeneratorStatuses}
                        />
                      }
                      label="Show/Hide All"
                      sx={{ margin: 0 }}
                    />
                    {!isGeneratorStatusFilterAtDefault && (
                      <Button onClick={handleResetGeneratorStatuses}>
                        Reset
                      </Button>
                    )}
                  </Box>
                  {GENERATOR_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox
                        checked={selectedGeneratorStatuses.includes(
                          option.value
                        )}
                      />
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* State Filter */}
              <FormControl variant="standard" size="small" sx={{ width: 140 }}>
                <InputLabel id="state-filter-label">State</InputLabel>
                <Select
                  labelId="state-filter-label"
                  open={stateOpen}
                  onOpen={handleStateOpen}
                  onClose={handleStateClose}
                  multiple
                  value={selectedStates}
                  onChange={handleStatesChange}
                  label="State"
                  variant="standard"
                  inputProps={{
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                    "data-1p-ignore": "true",
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return "None";
                    }
                    if (selected.length === US_STATES.length) {
                      return `All (${selected.length})`;
                    }
                    if (selected.length <= 3) {
                      return (selected as string[]).join(", ");
                    }
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{
                    MenuListProps: {
                      autoFocusItem:
                        selectedStates.length === US_STATES.length
                          ? false
                          : true,
                    },
                    PaperProps: {
                      sx: { minWidth: 280, maxHeight: 400 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      pl: 2,
                      pr: 1,
                      py: 0.75,
                      borderBottom: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedStates.length === US_STATES.length}
                          indeterminate={
                            selectedStates.length > 0 &&
                            selectedStates.length < US_STATES.length
                          }
                          onChange={handleToggleAllStates}
                        />
                      }
                      label="Show/Hide All"
                      sx={{ margin: 0 }}
                    />
                    {!isStateFilterAtDefault && (
                      <Button onClick={handleResetStates}>Reset</Button>
                    )}
                  </Box>
                  {US_STATES.map((state) => (
                    <MenuItem key={state} value={state}>
                      <Checkbox checked={selectedStates.includes(state)} />
                      {STATE_NAMES[state as keyof typeof STATE_NAMES] ?? state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Due Date Range Filter */}
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DateRangePicker
                  value={[dueDateStart, dueDateEnd]}
                  onChange={handleDueDateRangeChange}
                  localeText={{ start: "Due Start", end: "Due End" }}
                  slotProps={{
                    shortcuts: {
                      items: datePickerShortcuts,
                    },
                    textField: {
                      variant: "standard",
                      size: "small",
                      sx: { width: 105 },
                      inputProps: {
                        autoComplete: "off",
                        "data-lpignore": "true",
                        "data-form-type": "other",
                        "data-1p-ignore": "true",
                      },
                    },
                  }}
                  slots={{
                    field: MultiInputDateRangeField,
                  }}
                />
              </LocalizationProvider>

              {/* Toggles and Reset - using Switch pattern from waste submissions */}
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                sx={{ alignSelf: "flex-end", mb: 0.5 }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={assignedToMe}
                      onChange={handleAssignedToMeChange}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Assigned to Me"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                  }}
                />
                <Tooltip title="Includes overdue submissions from the past 3 months, regardless of due date filters. Items older than 3 months are excluded to focus on actionable work.">
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeOverdue}
                          onChange={handleIncludeOverdueChange}
                          color="primary"
                          size="small"
                        />
                      }
                      label="Include Overdue"
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": { fontSize: "0.875rem" },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      (since {overdueCutoffDate})
                    </Typography>
                  </Stack>
                </Tooltip>
                <Button
                  onClick={handleResetFilters}
                  variant="text"
                  size="small"
                  sx={{
                    visibility: areFiltersAtDefault ? "hidden" : "visible",
                  }}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" gap={0.5} alignItems="center">
              <Tooltip title="Export current filters to CSV (includes facility UUID)">
                <span>
                  <Button
                    onClick={handleExportCsv}
                    disabled={exportLoadingOrPolling}
                    variant="text"
                    size="small"
                    startIcon={<FileDownload />}
                  >
                    Export CSV
                  </Button>
                </span>
              </Tooltip>
              <Button onClick={handleToggle} variant="text" size="small">
                {statsOpen ? "Hide Stats" : "Show Stats"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}

      {/* Stats Panel */}
      {isOverviewRoute && (
        <Collapse in={statsOpen}>
          <Stack paddingX={3} paddingY={2}>
            <SummaryOverview
              filter={statsFilter}
              onlyOverdue={onlyOverdue}
              onOverdueClick={handleOverdueClick}
            />
          </Stack>
        </Collapse>
      )}

      {/* Main Data Grid */}
      {isOverviewRoute && (
        <Stack paddingX={3} paddingBottom={3}>
          <WasteSubmissionsDataGrid
            includeOverdue={includeOverdue}
            onlyOverdue={onlyOverdue}
            startDate={queryStartDate}
            endDate={queryEndDate}
            materializationTypes={materializationTypes}
            defaultSearch={defaultSearch}
          />
        </Stack>
      )}

      {/* Outlet for detail routes */}
      <Outlet />
    </Stack>
  );
}

export function WasteFulfillmentDashboard() {
  return (
    <StatsProvider>
      <OmnisearchProvider>
        <WasteFulfillmentDashboardContent />
      </OmnisearchProvider>
    </StatsProvider>
  );
}
